import { AppDispatch } from "app/store";
import OmronFooter from "components/OmronFooter";
import OmronHeader from "components/OmronHeader";
import OmronSidebar from "components/OmronSidebar";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { selectUserState } from "features/auth/selectors";
import {
  selectActivations,
  selectExcuteState,
  selectLoadState,
  selectMaxPage,
  selectPageIndex,
  selectPopupTarget,
} from "features/activation/selectors";
import { logoutAsync, userAsync } from "features/auth/operations";
import { useTranslation } from "react-i18next";
import PageTitle from "components/PageTitle";
import {
  changeNowIndex,
  changePopupTarget,
  clearPopupTarget,
  generatePermissionTicketsAsync,
  initialAsync,
  revokeAsync,
  updateAsync,
} from "features/activation";
import ActivationTable from "./ActivationTable";
import { RevokePopup } from "./RevokePopup";

const ActivationPage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector(selectUserState);

  const loadState = useSelector(selectLoadState);
  const excuteState = useSelector(selectExcuteState);
  const activations = useSelector(selectActivations);
  const maxpage = useSelector(selectMaxPage);
  const pageIndex = useSelector(selectPageIndex);
  const popupTarget = useSelector(selectPopupTarget);

  const onPageChange = useCallback(
    (x: { selected: number }) => {
      // storeのnowIndexを更新する。
      dispatch(changeNowIndex({ pegeNumber: x.selected }));
      // 選択された数字でAPIを叩く
      dispatch(updateAsync({ pageIndex: x.selected }));
    },
    [dispatch]
  );

  const onRevoke = useCallback(
    (aId: string) => {
      // storeのnowIndexを更新する。
      dispatch(changePopupTarget({ aId }));
    },
    [dispatch]
  );

  const onExcute = useCallback(
    async (aId: string, revocationTicket: ArrayBuffer) => {
      const { meta } = await dispatch(
        revokeAsync({
          aId,
          revocationTicket,
        })
      );
      if (meta.requestStatus === "fulfilled") {
        await dispatch(updateAsync({ pageIndex }));
      }
    },
    [dispatch, pageIndex]
  );

  const onGeneratePermissionTicket = useCallback(
    async (aId: string) => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        t("activationPage.text.generatePermissionTicketConfirm")
      );
      if (ok) {
        const { meta } = await dispatch(
          generatePermissionTicketsAsync({ aId })
        );
        if (meta.requestStatus === "fulfilled") {
          await dispatch(
            updateAsync({
              pageIndex,
            })
          );
        }
      }
    },
    [dispatch, pageIndex, t]
  );

  // 初期読込
  useEffect(() => {
    dispatch(userAsync());
    dispatch(initialAsync());
  }, [dispatch]);

  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);

  return (
    <div>
      {popupTarget && (
        <RevokePopup
          aId={popupTarget.aId}
          name={popupTarget.name}
          onCancel={() => {
            dispatch(clearPopupTarget());
          }}
          onExcute={onExcute}
          version={popupTarget.version}
          loading={excuteState}
        />
      )}
      <Wrap>
        <OmronHeader
          link={{ label: t("common.label.breadcrumbsHome"), path: "/" }}
          label={[t("common.label.breadcrumbsActivation")]}
          user={user}
        />
        <OmronSidebar activation onLogout={logout} />
        <Main>
          <PageTitle title={t("activationPage.label.title")} text="" />
          {/* TODO:検索機能を入れる */}
          <ActivationTable
            loadState={loadState}
            dataList={activations}
            maxPage={maxpage}
            pageIndex={pageIndex}
            onPageChange={onPageChange}
            onGeneratePermissionTicket={onGeneratePermissionTicket}
            onRevoke={onRevoke}
          />
        </Main>
        <OmronFooter />
      </Wrap>
    </div>
  );
};

export default ActivationPage;

const Wrap = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas: "header header" "breadc breadc" "side   main" "side   footer";
  min-height: 100vh;
`;
const Main = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;
